import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import Accordion from '../../components/Accordion';
import { useLocalStorage } from '@uidotdev/usehooks';
import { LoginResponseData } from '@EHSNavigator/backend';

interface FaqsProps {
  otherClasses?: string;
}

const Faqs: React.FC<FaqsProps> = ({ otherClasses }) => {
  const faqsClasses = clsx(otherClasses, 'px-15 md:px-6 lg:pl-8 pt-14 lg:pr-14 pb-9 w-full');
  const [userInfo] = useLocalStorage<LoginResponseData | null>('userInfo', null);
  const dispatch = useDispatch();

  const FAQ_MOCKS = [
    {
      visible: ['Owner'].includes(userInfo?.user?.role?.name ?? ''),
      title: 'Getting Started',
      faqs: [
        {
          title: 'What happens after an organization is registered?',
          description:
            'The registered user becomes the Owner. You get a 7-day free trial with access to all features and can onboard 10 users.',
        },
        {
          title: 'What happens after an organization signs up?',
          description:
            'After an organization signs up, the user who completes the registration is assigned the Owner role. The Owner can start configuring departments, adding Admins, and setting up the initial team. Admins can then add Managers and Employees.',
        },
      ],
    },
    {
      visible: ['Owner', 'Admin'].includes(userInfo?.user?.role?.name ?? ''),
      title: 'User Management',
      faqs: [
        {
          title: 'How do I add users to the platform?',
          description:
            'Users can be added via the User Management section, which is accessible by both the Owner and Admins.',
        },
        {
          title: 'Do I need to set anything up before adding users?',
          description:
            'Yes. You must first create at least one Department under User Management > Departments. Each user must be assigned to a department.',
        },
        {
          title: 'Can I change a user’s role after they’ve been added?',
          description:
            'Yes. Roles can be updated anytime via User Management > User Roles.',
        },
        {
          title: 'Who can manage users and roles?',
          description:
            'The Owner has full control over all users and roles. Admins can create, update, or perform delete operations on users and can also update their roles and department assignments',
        },
      ],
    },
    {
      visible: true,
      title: 'Incidents & Corrective Actions',
      faqs: [
        {
          title: 'Who can report incidents?',
          description:
            'Any user on the platform can report an incident via Incidents > Report Incident menu.',
        },
        {
          title: 'Can I save an incident report as a draft?',
          description:
            'Yes. Incident reports can be saved as drafts and submitted later.',
        },
        {
          title: 'Who gets notified when an incident is reported?',
          description:
            'The Owner, Admins, and Managers receive an email notification whenever a new incident is submitted',
        },
        {
          title: 'What happens after an incident is submitted?',
          description:
            'Management will review the incident. They can either mark it as Closed or assign a Corrective Action to a team or individual.',
        },
        {
          title: 'How do I track corrective actions assigned to me?',
          description:
            'Go to Corrective Actions > Assigned to Me to view, manage, mark for review, or request extensions.',
        },
        {
          title: 'Who gets notified when a corrective action is assigned?',
          description:
            'The assigned team or individual receives an email with full details of the corrective action.',
        },
        {
          title: 'What can I do if I’m assigned a corrective action?',
          description:
            'You can mark it for review, request an extension, or pull it back if needed.',
        },
        {
          title: 'Can I add supporting notes or files to a corrective action?',
          description:
            'Yes, you can add notes and assets (i.e., image, video, and document) for a corrective action mark for review or due date extension requests.',
        },
        {
          notVisible: ['Employee'].includes(userInfo?.user?.role?.name ?? ''),
          title: 'Can I save a corrective action without assigning it?',
          description:
            'Yes, corrective actions can be saved as drafts before being assigned.',
        },
        {
          notVisible: ['Employee'].includes(userInfo?.user?.role?.name ?? ''),
          title: 'Who can edit or assign a corrective action?',
          description:
            'The Owner, Admins, and Managers can edit any corrective action or issue a new one.',
        },
        {
          notVisible: ['Employee'].includes(userInfo?.user?.role?.name ?? ''),
          title: 'Can multiple users be assigned to a corrective action?',
          description:
            'Yes. One will be the Lead Assignee, responsible for sending it for review or requesting a due date extension.',
        },
        {
          notVisible: ['Employee'].includes(userInfo?.user?.role?.name ?? ''),
          title: 'Who can approve or reject a corrective action?',
          description:
            'Only the user who assigned it can approve, reject, or respond to extension requests.',
        },
      ],
    },
    {
      visible: ['Owner', 'Admin'].includes(userInfo?.user?.role?.name ?? ''),
      title: 'Bulk User Creation',
      faqs: [
        {
          title: 'Who can access the Bulk Upload feature?',
          description:
            'Only the Owner and Admins of an organization can access and use Bulk Upload.',
        },
        {
          title: 'What file formats are supported for bulk upload?',
          description: 'You can upload users using XLSX or CSV file formats.',
        },
        {
          title: 'Is there a sample file available?',
          description:
            'Yes, you can download a sample XLSX file with instructions before uploading.',
        },
        {
          title: 'What kind of validations are performed during upload?',
          description: (
            <>
              The system checks for:
              <ul className="my-4 flex list-disc flex-col gap-2 pl-4">
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Duplicate emails within the organization
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Correct email format
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Mandatory fields filled in, etc
                </li>
              </ul>
              All records must pass validations before proceeding.
            </>
          ),
        },
        {
          title: 'Can I edit or remove records after uploading the file?',
          description:
            'Yes, you can edit rows directly in the validation table or remove records before final submission.',
        },
        {
          title: 'Where can I see the status of my bulk upload?',
          description:
            'The Task Review Screen shows real-time progress, upload history (last 2 months), and details of successful/failed records.',
        },
        {
          title: 'Will I be notified after the bulk upload is complete?',
          description:
            'Yes, both the initiating Admin and the Owner receive email notifications with details of onboarded users.',
        },
      ],
    },
    {
      visible: ['Owner', 'Admin', 'Manager'].includes(
        userInfo?.user?.role?.name ?? '',
      ),
      title: 'Injuries (Investigation Tool)',
      faqs: [
        {
          title: 'Who can access the Investigation Tool?',
          description:
            'The feature is available to Owner, Admin, and Manager roles.',
        },
        {
          title: 'How are injuries tracked in the system?',
          description:
            'When an incident is reported under Injury/Illness type, it automatically appears in the Injuries section.',
        },
        {
          title: 'Can I upload investigation reports?',
          description:
            'Yes, you can upload PDF/DOC reports. If uploaded, only OSHA-required fields will remain in later steps.',
        },
        {
          title: 'What investigation methods are supported?',
          description: (
            <>
              The tool supports:
              <ul className="my-4 flex list-disc flex-col gap-2 pl-4">
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  General Injury Details
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Personal Formatted Investigation Report
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Guided 5 Whys questions; a few are required in the OSHA
                  submission
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Custom organizational questions (set in My Account {`>`}{' '}
                  Custom Questions)
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Additional custom, incident-specific questions
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Root Cause Analysis summary
                </li>
              </ul>
            </>
          ),
        },
        {
          title: 'Can investigations be edited after submission?',
          description:
            'Yes, investigations can be edited. Management is also notified by email whenever an investigation is submitted.',
        },
        {
          title: 'Does this integrate with OSHA reporting?',
          description:
            'Yes, investigations and records are compatible and will populate the OSHA module.',
        },
        {
          title: 'Are injury statistics available?',
          description:
            'Yes, the statistics dashboard shows TRIR, time since last injury, types of loss, body parts affected, classifications, and injuries by department.',
        },
      ],
    },
    {
      visible: ['Owner', 'Admin'].includes(userInfo?.user?.role?.name ?? ''),
      title: 'OSHA Reporting',
      faqs: [
        {
          title: 'Who can submit OSHA reports?',
          description:
            'Only organization owners and admins have permission to submit OSHA Forms.',
        },
        {
          title: ' When is OSHA reporting available?',
          description:
            'OSHA reporting is only open from January 2 to March 2 each year, in accordance with OSHA’s official submission window.',
        },
        {
          title: 'What forms are supported?',
          description:
            'Currently, Form 300 (Injury/Illness Log) and Form 300A (Annual Summary) are supported.',
        },
        {
          title: 'Can I review the data before submission?',
          description:
            'Yes. You can review and edit form data before final submission.',
        },
        {
          title: 'What happens if a submission fails?',
          description:
            'Failed submissions will appear in the OSHA Logs screen. You can review the error and resubmit after correction.',
        },
        {
          title: 'How can I exclude specific records from OSHA submission?',
          description:
            'Records can be excluded by flagging them as “Do not submit” within the OSHA 300 screen.',
        },
        {
          title: 'Where can I track submitted reports?',
          description:
            'Use the OSHA Logs screen to view all submissions, filter by date or status, and export reports if needed.',
        },
      ],
    },
    {
      visible: true,
      title: 'Document Library',
      faqs: [
        {
          title: 'What file types can I upload?',
          description:
            'You can upload most common document types such as .pdf, .doc, and .docx.',
        },
        {
          title: 'Can I see documents uploaded by other users?',
          description:
            'Yes, all users can access organizational documents. Personal documents are visible only to their uploader.',
        },
        {
          title: 'How does the search enhancement work?',
          description:
            'The system now analyzes document content to return more accurate and relevant results — not just by file title or tags.',
        },
        {
          title: 'Who can delete documents?',
          description:
            'Users can delete their own documents. Admins can delete any document if required.',
        },
      ],
    },
  ];
  return (
    <div className={faqsClasses} data-testid="faqs">
      <div className="border-b-neutral-tones-text/35 border-b pb-6 lg:border-b-0 lg:pb-0">
        <h3 className="text-brand-byzantine hidden lg:block">FAQs</h3>
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
          Everything you need to know before getting started or reaching out to
          our team.
        </p>
        <div className="flex items-center gap-3 lg:hidden">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <h3 className="text-brand-byzantine lg:hidden">FAQs</h3>
        </div>
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
          Everything you need to know before getting started or reaching out to
          our team.
        </p>
      </div>
      <div className="mt-6 flex flex-col gap-10 lg:mt-12">
        {FAQ_MOCKS?.map(
          ({ title, faqs, visible }, index) =>
            visible && (
              <div key={index} className="">
                <h4 className="mb-4">{title}</h4>
                <div className="flex flex-col gap-6">
                  {faqs?.map(
                    (
                      node: {
                        title: string;
                        description: string | ReactNode;
                        notVisible?: boolean;
                      },
                      index,
                    ) =>
                      !node?.notVisible ? (
                        <Accordion {...node} key={index} />
                      ) : null,
                  )}
                </div>
              </div>
            ),
        )}
      </div>
    </div>
  );
};

export default Faqs;
